import React, { useState, useEffect, useRef } from 'react';
import { PencilIcon, TrashIcon, SearchIcon, CalendarIcon, PlusIcon, DocumentDownloadIcon, ChevronLeftIcon, ChevronRightIcon, ExclamationCircleIcon, BanIcon } from '@heroicons/react/outline';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import toastService from '../../../services/toast';
import usersService from '../../../services/users';
import AlertConfirmModal from '../../common/AlertConfirmModal';
import FormModal from '../../../components/common/FormModal';
import { ROLES } from '../../../services/permissionsService';

const InstructorPage = () => {
  const { hasPermission } = usePermissions();
  const searchTimeoutRef = useRef(null);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]); // قائمة الأدوار المتاحة من السيرفر
  const [newInstructor, setNewInstructor] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    status: 'نشط',
    role: ROLES.INSTRUCTOR // قيمة افتراضية لحين استلام الأدوار من السيرفر
  });
  const [newPhoneError, setNewPhoneError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [phoneError, setPhoneError] = useState('');
  const [showBanModal, setShowBanModal] = useState(false);
  const [instructorToBlock, setInstructorToBlock] = useState(null);
  const dateFromRef = useRef(null);
  const dateToRef = useRef(null);

  useEffect(() => {
    fetchAvailableRoles();
    loadInstructors(1);
  }, []);

  useEffect(() => {
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, []);

  const fetchAvailableRoles = async () => {
    try {
      const rolesData = await usersService.fetchRoles();
      if (rolesData && Array.isArray(rolesData.data)) {
        setAvailableRoles(rolesData.data);
        if (rolesData.data.length > 0) {
          const instructorRole = rolesData.data.find(role => role.name === ROLES.INSTRUCTOR);
          const defaultRoleId = instructorRole?.id || rolesData.data[0].id;
          setNewInstructor(prev => ({ ...prev, role_id: defaultRoleId }));
        }
      }
    } catch (error) {
      toastService.error('حدث خطأ في جلب الأدوار المتاحة');
    }
  };

  const loadInstructors = async (page, search = null, from = null, to = null) => {
    setLoading(true);
    try {
      const params = { page, per_page: 20 };
      if (search) params.search = search;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const response = await usersService.fetchInstructors(params);
      setInstructors(response.data);
      setLastPage(response.last_page);
      setCurrentPage(response.current_page);
    } catch (error) {
      toastService.error('حدث خطأ أثناء تحميل بيانات المدربين');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value.trim()) { setSearching(false); loadInstructors(1); return; }
    setSearching(true);
    searchTimeoutRef.current = setTimeout(() => {
      loadInstructors(1, value.trim(), dateFrom, dateTo);
      setSearching(false);
    }, 300);
  };

  const handleDateFromChange = (e) => {
    const value = e.target.value;
    setDateFrom(value);
    setCurrentPage(1);
    loadInstructors(1, searchQuery, value, dateTo);
  };

  const handleDateToChange = (e) => {
    const value = e.target.value;
    setDateTo(value);
    setCurrentPage(1);
    loadInstructors(1, searchQuery, dateFrom, value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadInstructors(page, searchQuery, dateFrom, dateTo);
  };

  const handleExportToExcel = () => {
    const dataToExport = instructors.map(instructor => ({
      'الاسم': instructor.name,
      'البريد الإلكتروني': instructor.email,
      'رقم الجوال': instructor.phone,
      'تاريخ التسجيل': instructor.createdAt.split('-').map((part, index) => 
        index === 2 ? part : part.padStart(2, '0')).join('-'),
      'الحالة': instructor.status
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "المدربين");
    XLSX.writeFile(wb, "instructors-report.xlsx");
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) handlePageChange(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < lastPage) handlePageChange(currentPage + 1);
  };

  const handleAddInstructor = async (e) => {
    e.preventDefault();
    try {
      const errors = [];
      
      if (!newInstructor.name?.trim()) {
        errors.push("الرجاء إدخال اسم المدرب");
      }
      
      if (!newInstructor.email?.trim()) {
        errors.push("الرجاء إدخال البريد الإلكتروني");
      } else if (!/\S+@\S+\.\S+/.test(newInstructor.email)) {
        errors.push("البريد الإلكتروني غير صالح");
      }
      
      if (!newInstructor.phone?.trim()) {
        errors.push("الرجاء إدخال رقم الجوال");
      } else if (newPhoneError) {
        errors.push(newPhoneError);
      }
      
      if (!newInstructor.password?.trim()) {
        errors.push("الرجاء إدخال كلمة المرور");
      }
      
      if (!newInstructor.role_id) {
        errors.push("الرجاء تحديد الدور");
      }
      
      if (errors.length > 0) {
        toastService.error(
          <div className="text-right" dir="rtl">
            <p className="font-bold mb-2">يرجى تصحيح الأخطاء التالية:</p>
            <ul className="list-disc mr-5">
              {errors.map((err, index) => (
                <li key={index} className="text-right">{err}</li>
              ))}
            </ul>
          </div>
        );
        return;
      }

      const response = await usersService.createInstructor(newInstructor);
      toastService.success('تم إضافة المستخدم بنجاح');
      setShowAddModal(false);
      
      // تحديث داخلي للقائمة بدلاً من إعادة التحميل الكامل
      if (response.data && response.data.user) {
        // إضافة المستخدم الجديد إلى القائمة
        setInstructors(prevInstructors => [response.data.user, ...prevInstructors]);
      } else {
        // في حالة عدم استجابة المستخدم بالشكل المتوقع، نقوم بإعادة التحميل
        loadInstructors(1);
      }
      
      // إعادة تعيين نموذج الإضافة
      setNewInstructor({
        name: '',
        email: '',
        phone: '',
        password: '',
        status: 'نشط',
        role_id: availableRoles.length > 0 ? availableRoles.find(role => role.name === ROLES.INSTRUCTOR)?.id || availableRoles[0].id : null
      });
    } catch (error) {
      let errorMessage = error;
      
      // تحقق إذا كان الخطأ بسبب تكرار رقم الجوال
      if (error.includes('Duplicate entry') && error.includes('phone_unique')) {
        errorMessage = 'رقم الجوال مستخدم بالفعل. الرجاء إدخال رقم جوال آخر.';
      }

      toastService.error(
        <div className="text-right" dir="rtl">
          <p className="font-bold mb-2">حدث خطأ:</p>
          <ul className="list-disc mr-5">
            {errorMessage.split('\n').map((err, index) => (
              <li key={index} className="text-right">{err}</li>
            ))}
          </ul>
        </div>
      );
    }
  };

  // دالة لفتح نافذة التعديل
  const handleEditClick = (instructor) => {
    // نسخة من المدرب لكن بدون دور محدد
    const instructorWithEmptyRole = {
      ...instructor,
      role_id: '', // تفريغ الدور
      role: '',    // تفريغ الدور
    };
    
    setSelectedInstructor(instructorWithEmptyRole);
    setShowEditModal(true);
  };

  // دالة لفتح نافذة تأكيد الحذف
  const handleDeleteClick = (instructor) => {
    setSelectedInstructor(instructor);
    setShowDeleteModal(true);
  };

  // دالة للتحقق من صحة البيانات
  const validateForm = (instructor, isUpdate = false) => {
    const errors = [];
    
    // التحقق من وجود الاسم
    if (!instructor?.name || instructor.name.trim() === '') {
      errors.push('الاسم مطلوب');
    }

    // التحقق من وجود البريد الإلكتروني
    if (!instructor?.email || instructor.email.trim() === '') {
      errors.push('البريد الإلكتروني مطلوب');
    } else if (!/\S+@\S+\.\S+/.test(instructor.email)) {
      errors.push('البريد الإلكتروني غير صالح');
    }

    // التحقق من وجود رقم الجوال
    if (!instructor?.phone || instructor.phone.trim() === '') {
      errors.push('رقم الجوال مطلوب');
    }
    
    // التحقق من وجود الدور
    if (!instructor?.role_id) {
      errors.push('الدور مطلوب');
    }

    return errors;
  };

  // دالة لحفظ التعديلات
  const handleUpdateInstructor = async () => {
    try {
      const errors = validateForm(selectedInstructor, true);
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        toastService.error(
          <div className="text-right" dir="rtl">
            <p className="font-bold mb-2">يرجى تصحيح الأخطاء التالية:</p>
            <ul className="list-disc mr-5">
              {Object.values(errors).map((error, index) => (
                <li key={index} className="text-right">{error}</li>
              ))}
            </ul>
          </div>
        );
        return;
      }

      const { id, password, ...instructorData } = selectedInstructor;
      
      // تأكد من أن role_id موجود وصالح
      if (instructorData.role_id) {
        // تحويل role_id إلى رقم للتأكد من أنه ليس نص
        instructorData.role_id = parseInt(instructorData.role_id);
        // إضافة حقل role للتوافق مع الباك إند
        const selectedRole = availableRoles.find(role => role.id === instructorData.role_id);
        if (selectedRole) {
          instructorData.role = selectedRole.name;
        }
      }
      
      // 1. تحديث بيانات المدرب الأساسية (بدون كلمة المرور)
      const response = await usersService.updateInstructor(id, instructorData);
      
      // 2. لو فيه كلمة مرور جديدة، استدعي API المخصصة لتغيير الباسورد للمدربين
      if (password && password.trim() !== '') {
        // Change instructor password
        await usersService.changeInstructorPassword(id, { password });
        // Password changed successfully
      }
      
      toastService.success('تم تحديث بيانات المدرب بنجاح');
      setShowEditModal(false);
      
      // التحقق من تغيير الدور
      const newRole = instructorData.role; // الدور الجديد للمستخدم
      
      // تحقق مما إذا كان الدور قد تغير من مدرب إلى دور آخر
      // إذا كان الدور ليس مدرب، فهذا يعني أنه تم نقله لدور آخر
      const isRoleChanged = newRole && newRole !== ROLES.INSTRUCTOR;
      
      if (isRoleChanged) {
        // إذا تغير الدور من مدرب إلى دور آخر، نحذف المستخدم من القائمة
        setInstructors(prevInstructors => prevInstructors.filter(instructor => instructor.id !== id));
      } else {
        // إذا لم يتغير الدور أو تغير لدور مدرب آخر، نحدث البيانات فقط
        if (response.data && response.data.user) {
          // تحديث المستخدم في القائمة باستخدام البيانات من السيرفر
          setInstructors(prevInstructors => 
            prevInstructors.map(instructor => 
              instructor.id === id ? response.data.user : instructor
            )
          );
        } else {
          // حتى لو ما رجعش بيانات من السيرفر، نحدث القائمة بالبيانات المرسلة
          setInstructors(prevInstructors => 
            prevInstructors.map(instructor => 
              instructor.id === id ? { ...instructor, ...instructorData } : instructor
            )
          );
        }
      }
      
      setFormErrors({});
    } catch (error) {
      toastService.error(
        <div className="text-right" dir="rtl">
          <p className="font-bold mb-2">حدث خطأ:</p>
          <ul className="list-disc mr-5">
            {error.split('\n').map((err, index) => (
              <li key={index} className="text-right">{err}</li>
            ))}
          </ul>
        </div>
      );
    }
  };

  // دالة لتأكيد الحذف
  const handleDeleteInstructor = async () => {
    try {
      const idToDelete = selectedInstructor.id;
      await usersService.deleteInstructor(idToDelete);
      toastService.success('تم حذف المدرب بنجاح');
      setShowDeleteModal(false);
      setSelectedInstructor(null);
      
      // تحديث داخلي للقائمة بحذف المدرب منها
      setInstructors(prevInstructors => 
        prevInstructors.filter(instructor => instructor.id !== idToDelete)
      );
    } catch (error) {
      toastService.error(
        <div className="text-right" dir="rtl">
          <p className="font-bold mb-2">حدث خطأ:</p>
          <ul className="list-disc mr-5">
            {error.split('\n').map((err, index) => (
              <li key={index} className="text-right">{err}</li>
            ))}
          </ul>
        </div>
      );
    }
  };

  // دالة لفتح نافذة تأكيد الحظر
  const handleBanClick = (instructor) => {
    setInstructorToBlock(instructor);
    setShowBanModal(true);
  };

  // دالة لتأكيد الحظر
  const handleBanInstructor = async () => {
    try {
      const idToUpdate = instructorToBlock.id;
      const newStatus = instructorToBlock.status !== 'محظور' ? 'محظور' : 'نشط';
      const response = await usersService.changeInstructorStatus(idToUpdate, instructorToBlock.status !== 'محظور');
      toastService.success(`تم ${instructorToBlock.status === 'محظور' ? 'إلغاء حظر' : 'حظر'} المدرب بنجاح`);
      setShowBanModal(false);
      
      // تحديث داخلي للقائمة بتغيير حالة المدرب
      if (response.data && response.data.user) {
        // تحديث المستخدم في القائمة
        setInstructors(prevInstructors => 
          prevInstructors.map(instructor => 
            instructor.id === idToUpdate ? response.data.user : instructor
          )
        );
      } else {
        // تحديث الحالة بناءً على المعلومات المتوفرة
        setInstructors(prevInstructors => 
          prevInstructors.map(instructor => 
            instructor.id === idToUpdate ? {...instructor, status: newStatus} : instructor
          )
        );
      }
      
      setInstructorToBlock(null);
    } catch (error) {
      toastService.error(
        <div className="text-right" dir="rtl">
          <p className="font-bold mb-2">حدث خطأ:</p>
          <ul className="list-disc mr-5">
            {error.split('\n').map((err, index) => (
              <li key={index} className="text-right">{err}</li>
            ))}
          </ul>
        </div>
      );
    }
  };

  // تم إزالة حالة اللودنج

  return (
    <div className="p-6">
      {/* Search and Filter Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6">
        {/* Search Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">قائمة المدربين</h2>
          <div className="flex gap-4">
            {hasPermission && hasPermission('create_instructor') && (
              <div className="relative group">
                <PlusIcon 
                  className="h-6 w-6 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 cursor-pointer transition-colors duration-200"
                  onClick={() => setShowAddModal(true)}
                />
                <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -left-4 -translate-x-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                  إضافة مدرب جديد
                </div>
              </div>
            )}
            
            <div className="relative group">
              <DocumentDownloadIcon 
                className="h-6 w-6 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400 cursor-pointer transition-colors duration-200"
                onClick={handleExportToExcel}
              />
              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -left-4 -translate-x-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                استخراج في ملف إكسل
              </div>
            </div>
          </div>
        </div>
        
        {/* Search Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Search Input */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">البحث عن مدرب</label>
                <div className="relative rounded-lg">
                <input
                  type="text"
                  placeholder="البحث عن مدرب..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="input-base pr-10"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  {searching ? (
                    <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <SearchIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Date From */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">من تاريخ</label>
              <div className="relative rounded-lg">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={handleDateFromChange}
                  className="input-base pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-10"
                  ref={(input) => {
                    if (input) {
                      dateFromRef.current = input;
                    }
                  }}
                />
                <div 
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  onClick={() => dateFromRef.current?.showPicker()}
                >
                  <CalendarIcon className="h-5 w-5 text-green-500 dark:text-green-400" />
                </div>
              </div>
            </div>

            {/* Date To */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">إلى تاريخ</label>
              <div className="relative rounded-lg">
                <input
                  type="date"
                  value={dateTo}
                  onChange={handleDateToChange}
                  className="input-base pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-10"
                  ref={(input) => {
                    if (input) {
                      dateToRef.current = input;
                    }
                  }}
                />
                <div 
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  onClick={() => dateToRef.current?.showPicker()}
                >
                  <CalendarIcon className="h-5 w-5 text-orange-500 dark:text-orange-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">إجمالي المدربين</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{instructors.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">المدربين الغير نشطين</div>
          <div className="mt-1 text-2xl font-semibold text-yellow-600 dark:text-yellow-400">
            {instructors.filter(i => i.status === 'غير نشط').length}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">المدربين المحظورين</div>
          <div className="mt-1 text-2xl font-semibold text-red-600 dark:text-red-400">
            {instructors.filter(i => i.status === 'محظور').length}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className={`${instructors.length > 0 ? 'overflow-x-auto' : ''}`}>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-center" style={{ tableLayout: 'fixed' }}>
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="w-40 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  الاسم
                </th>
                <th scope="col" className="w-44 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  البريد الإلكتروني
                </th>
                <th scope="col" className="w-40 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  رقم الجوال
                </th>
                <th scope="col" className="w-40 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  تاريخ التسجيل
                </th>
                <th scope="col" className="w-32 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  الحالة
                </th>
                <th scope="col" className="w-24 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={`skeleton-${i}`} className="animate-pulse">
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="flex gap-3 justify-center">
                        <div className="h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded-full" />
                        <div className="h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded-full" />
                        <div className="h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded-full" />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                instructors.map((instructor) => (
                  <tr key={instructor.id}>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{instructor.name}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{instructor.email}</div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="text-sm text-gray-900 dark:text-gray-100" dir="ltr">
                        {instructor.phone}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="text-sm text-gray-900 dark:text-gray-100" dir="ltr">
                        {instructor.createdAt.split('-').map((part, index) => 
                          index === 2 ? part : part.padStart(2, '0')).join('-')}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                        instructor.status === 'نشط' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : instructor.status === 'غير نشط'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {instructor.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <div className="flex justify-end space-x-2 space-x-reverse">
                        {/* زر الحظر/فك الحظر */}
                        {hasPermission && hasPermission('edit_instructor') && (
                          <div className="relative group">
                            <BanIcon
                              className={`h-5 w-5 cursor-pointer ${
                                instructor.status === 'محظور'
                                  ? 'text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400'
                                  : 'text-yellow-600 hover:text-yellow-700 dark:text-yellow-500 dark:hover:text-yellow-400'
                              }`}
                              onClick={() => {
                                setInstructorToBlock(instructor);
                                setShowBanModal(true);
                              }}
                            />
                            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 text-xs bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50">
                              {instructor.status === 'محظور' ? 'فك حظر المدرب' : 'حظر المدرب'}
                            </div>
                          </div>
                        )}
                        {hasPermission && hasPermission('edit_instructor') && (
                          <PencilIcon 
                            className="h-5 w-5 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 cursor-pointer" 
                            onClick={() => handleEditClick(instructor)}
                          />
                        )}
                        {hasPermission && hasPermission('delete_instructor') && (
                          <TrashIcon 
                            className="h-5 w-5 text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 cursor-pointer" 
                            onClick={() => handleDeleteClick(instructor)}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
              <tr>
                <td colSpan="7">
                  <div className="flex justify-between items-center mt-2 mb-2">
                    <div className="flex justify-center items-center mx-auto">
                      <button 
                        className="p-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                      >
                        <ChevronRightIcon className="h-6 w-6" />
                      </button>
                      <span className="mx-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {currentPage} / {lastPage}
                      </span>
                      <button 
                        className="p-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
                        onClick={handleNextPage}
                        disabled={currentPage === lastPage}
                      >
                        <ChevronLeftIcon className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      {/* نافذة منبثقة لإضافة مدرب جديد */}
      <FormModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setNewInstructor({ name: '', email: '', phone: '', password: '', status: 'نشط', role: '' });
          setNewPhoneError('');
        }}
        onSubmit={handleAddInstructor}
        title="إضافة مدرب جديد"
        submitText="إضافة"
        cancelText="إلغاء"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              الاسم <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newInstructor.name}
              onChange={(e) => setNewInstructor({...newInstructor, name: e.target.value})}
              className="input-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              البريد الإلكتروني <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={newInstructor.email}
              onChange={(e) => setNewInstructor({...newInstructor, email: e.target.value})}
              className="input-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              رقم الجوال <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              maxLength="10"
              value={newInstructor.phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value === '') {
                  setNewInstructor({...newInstructor, phone: value});
                  setNewPhoneError('');
                } else if (value.length <= 2) {
                  setNewInstructor({...newInstructor, phone: value});
                  if (value.length === 2 && value !== '05') {
                    setNewPhoneError('رقم الجوال يجب أن يبدأ بـ 05');
                  } else {
                    setNewPhoneError('');
                  }
                } else if (value.startsWith('05')) {
                  setNewInstructor({...newInstructor, phone: value});
                  setNewPhoneError('');
                }
              }}
              className={`input-base ${newPhoneError ? 'border-red-500' : ''}`}
              placeholder="05xxxxxxxx"
              dir="ltr"
            />
            {newPhoneError && (
              <p className="mt-1 text-sm text-red-500">{newPhoneError}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              الدور <span className="text-red-500">*</span>
            </label>
            <select
              value={newInstructor.role_id || ''}
              onChange={(e) => setNewInstructor({...newInstructor, role_id: parseInt(e.target.value)})}
              className="input-base"
            >
              {availableRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.display_name || role.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              كلمة المرور <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={newInstructor.password}
              onChange={(e) => setNewInstructor({...newInstructor, password: e.target.value})}
              className="input-base"
            />
          </div>
        </div>
      </FormModal>

      {/* نافذة تعديل بيانات المدرب */}
      {showEditModal && selectedInstructor && (
        <FormModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedInstructor(null);
            setPhoneError('');
          }}
          onSubmit={handleUpdateInstructor}
          title="تعديل بيانات المدرب"
          submitText="حفظ"
          cancelText="إلغاء"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الاسم <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={selectedInstructor.name}
                onChange={(e) => setSelectedInstructor({...selectedInstructor, name: e.target.value})}
                className="input-base"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                البريد الإلكتروني <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={selectedInstructor.email}
                onChange={(e) => setSelectedInstructor({...selectedInstructor, email: e.target.value})}
                className="input-base"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                رقم الجوال <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                maxLength="10"
                value={selectedInstructor.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value === '') {
                    setSelectedInstructor({...selectedInstructor, phone: value});
                    setPhoneError('');
                  } else if (value.length <= 2) {
                    setSelectedInstructor({...selectedInstructor, phone: value});
                    if (value.length === 2 && value !== '05') {
                      setPhoneError('رقم الجوال يجب أن يبدأ بـ 05');
                    } else {
                      setPhoneError('');
                    }
                  } else if (value.startsWith('05')) {
                    setSelectedInstructor({...selectedInstructor, phone: value});
                    setPhoneError('');
                  }
                }}
                className={`input-base ${phoneError ? 'border-red-500' : ''}`}
                placeholder="05xxxxxxxx"
                dir="ltr"
              />
              {phoneError && (
                <p className="mt-1 text-sm text-red-500">{phoneError}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الدور <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedInstructor?.role_id || ''}
                onChange={(e) => {
                  // التحقق من أن القيمة ليست فارغة قبل التحويل إلى رقم
                  const roleId = e.target.value ? parseInt(e.target.value) : '';
                  setSelectedInstructor({...selectedInstructor, role_id: roleId})
                }}
                className="input-base"
              >
                <option value="">اختر الدور المناسب</option>
                {availableRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.display_name || role.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                كلمة المرور (اختياري)
              </label>
              <input
                type="password"
                value={selectedInstructor.password || ''}
                onChange={(e) => setSelectedInstructor({...selectedInstructor, password: e.target.value})}
                placeholder="اتركها فارغة للاحتفاظ بكلمة المرور الحالية"
                className="input-base"
              />
            </div>
          </div>
        </FormModal>
      )}
      {/* نافذة تأكيد الحذف */}
      {showDeleteModal && selectedInstructor && (
        <AlertConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedInstructor(null);
          }}
          onConfirm={handleDeleteInstructor}
          icon={ExclamationCircleIcon}
          iconColorClass="text-red-500"
          title="تأكيد حذف المدرب"
          message="هل أنت متأكد من حذف المدرب؟"
          details={selectedInstructor.name}
          note="سيتم حذف المدرب نهائياً من النظام"
          confirmText="تأكيد الحذف"
          cancelText="إلغاء"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
          noteClass="text-red-600 dark:text-red-400"
        />
      )}
      {/* نافذة تأكيد الحظر */}
      {showBanModal && instructorToBlock && (
        <AlertConfirmModal
          isOpen={showBanModal}
          onClose={() => {
            setShowBanModal(false);
            setInstructorToBlock(null);
          }}
          onConfirm={handleBanInstructor}
          icon={BanIcon}
          iconColorClass={instructorToBlock.status === 'محظور' ? 'text-green-500' : 'text-yellow-500'}
          title={instructorToBlock.status === 'محظور' ? 'تأكيد فك حظر المدرب' : 'تأكيد حظر المدرب'}
          message={instructorToBlock.status === 'محظور' ? 'هل أنت متأكد من فك حظر المدرب؟' : 'هل أنت متأكد من حظر المدرب؟'}
          details={instructorToBlock.name}
          note={instructorToBlock.status === 'محظور' ? 'سيتمكن المدرب من الوصول إلى النظام' : 'سيتم منع المدرب من الوصول إلى النظام'}
          confirmText={instructorToBlock.status === 'محظور' ? 'تأكيد فك الحظر' : 'تأكيد الحظر'}
          cancelText="إلغاء"
          confirmButtonClass={instructorToBlock.status === 'محظور' ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'}
          noteClass={instructorToBlock.status === 'محظور' ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}
        />
      )}
    </div>
  );
};

export default InstructorPage;
